import { ISOPreviousPlateData, ISOWellIngredientDTO } from 'business/models/CustomModels';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  ConditionDTO,
  ConditionIngredientDTO,
  CustomLayerDTO,
  InspectionSetDTO,
  LayerDTO,
  LayerStateDTO,
  LayerTypeDTO,
  ScoreDefinitionDTO,
  ScreenLayerDTO,
  WellLayerDTO,
  WellLayerIngredientDTO,
} from 'business/models/Models';
import { generate } from 'business/plateviewer/Generate/GenerateCommon';
import PlateService from 'business/services/PlateService';
import { getLatestInspection } from 'components/commandpanel/Navigator/DRCIPWUtil';
import {
  getIngredientName,
  getIngredientStock,
  getIngredientTypeName,
  getNewId,
  isNullOrUndefined,
} from 'utils/Global';
import { copyLayer } from '../PasteLayerHelper';
import { ISOIngrdientInfo } from './ISOIngredientInfo';
import { OptimizationIngredientName } from './OptimizationWizard';
import {
  Plate,
  ScoresOfWellDrops,
  WellIng,
  WellLayer,
} from 'components/Experiment/constants';

export async function validateAllWellScores(
  docData: ExperimentDtoClient,
  scoreDefinitions: ScoreDefinitionDTO[],
) {
  const iterativeScoregroupId = scoreDefinitions.find(
    (sd) => sd.scoreGroupName == 'Default',
  ).wellDropScoreGroupId;
  const latestInspectionId = getLatestImageBatchId(docData);
  const scoresData = await getExperimentScoresData(docData.plateDto);
  const iterativeScoresDictionary = {};

  for (let index = 0; index < scoresData?.length; index++) {
    const wellScore = scoresData[index];
    const iterativeScore = wellScore.find(
      (ws) =>
        ws.dropNumber == 1 &&
        ws.wellDropScoreGroupId == iterativeScoregroupId &&
        ws.scoreBatchId === latestInspectionId,
    );

    if (!isNullOrUndefined(iterativeScore)) {
      iterativeScoresDictionary[iterativeScore.wellNumber] = iterativeScore;
    }
  }

  const wellsWithLayers = getExperimentWellList(docData.plateDto).filter(
    (wl) => wl.wellLayerList.length > 0,
  );
  for (let j = 0; j < wellsWithLayers.length; j++) {
    const wellIngredient = wellsWithLayers[j];
    if (isNullOrUndefined(iterativeScoresDictionary[wellIngredient.wellNumber])) {
      return false;
    }
  }

  return true;
}

export async function getPrecipetantIngredientRows(
  docData: ExperimentDtoClient,
  scoreDefinitions: ScoreDefinitionDTO[],
  prevPlateData: ISOPreviousPlateData,
) {
  const conditionListLayers = docData.layers.filter(
    (l) => l.type === LayerTypeDTO.ConditionList,
  );
  const screenLayers = docData.layers.filter((l) => l.type === LayerTypeDTO.Screen);

  if (conditionListLayers.length > 0) {
    return await getIngredientInfoListForLayer(
      conditionListLayers,
      docData,
      scoreDefinitions,
      prevPlateData,
    );
  } else {
    return await getIngredientInfoListForLayer(
      screenLayers,
      docData,
      scoreDefinitions,
      prevPlateData,
    );
  }
}

async function getIngredientInfoListForLayer(
  targetLayers: LayerDTO[],
  docData: ExperimentDtoClient,
  scoreDefinitions: ScoreDefinitionDTO[],
  prevPlateData: ISOPreviousPlateData,
): Promise<ISOIngrdientInfo[]> {
  const ingredientInfoList: ISOIngrdientInfo[] = [];
  const latestInspectionId = getLatestImageBatchId(docData);
  const iterativeScoregroupId = scoreDefinitions.find(
    (sd) => sd.scoreGroupName == 'Default',
  ).wellDropScoreGroupId;

  for (let i = 0; i < targetLayers.length; i++) {
    const layer = targetLayers[i];
    const wellList = getExperimentWellList(docData.plateDto).filter(
      (wl) =>
        !isNullOrUndefined(wl.wellLayerList.find((wll) => wll.layerId === layer.id)),
    );

    for (let j = 0; j < wellList.length; j++) {
      const wellLayerRow = wellList[j].wellLayerList.find(
        (wll) => wll.layerId === layer.id,
      );
      const wellNumber = wellList[j].wellNumber;
      const imageScore = getImageScores(
        await getExperimentScoresData(docData.plateDto),
        iterativeScoregroupId,
        latestInspectionId,
        wellNumber,
      );

      for (let k = 0; k < wellLayerRow.wellLayerIngredientList.length; k++) {
        const wellLayerIngredient = wellLayerRow.wellLayerIngredientList[k];
        if (wellLayerIngredient.ingredientTypeName !== 'Precipitant') {
          continue;
        }
        const ingredientInfo: ISOIngrdientInfo = new ISOIngrdientInfo();
        ingredientInfo.LayerType = docData.layers.find(
          (lyr) => lyr.name === layer.name,
        ).type;
        ingredientInfo.sourceIngredientRow = {
          ingredient: wellLayerIngredient,
          layerName: layer.name,
          wellNumber: wellNumber,
        } as ISOWellIngredientDTO;

        if (isNullOrUndefined(imageScore)) {
          setDefaultScore(ingredientInfo, scoreDefinitions);
        } else {
          //set Default Score

          const wellDropScoreRow = scoreDefinitions.find(
            (s) => s.wellDropScoreId === imageScore.wellDropScoreId,
          );
          if (!isNullOrUndefined(wellDropScoreRow.isoValue)) {
            ingredientInfo.sourceScoreValue = wellDropScoreRow.isoValue;
          } else {
            ingredientInfo.sourceScoreValue = 0;
          }

          ingredientInfo.sourceScoreKey = getScoreISOKey(wellDropScoreRow.scoreName);
        }
        getPrecipitantIngredientForPreviousPlate(
          layer.name,
          ingredientInfo,
          prevPlateData,
          wellNumber,
          docData,
          iterativeScoregroupId,
          scoreDefinitions,
        );
        ingredientInfoList.push(ingredientInfo);
      }
    }
  }
  return ingredientInfoList;
}

function getPrecipitantIngredientForPreviousPlate(
  layerName: string,
  ingredientInfo: ISOIngrdientInfo,
  prevPlateData: ISOPreviousPlateData,
  wellNumber: number,
  docData: ExperimentDtoClient,
  iterativeScoregroupId: number,
  scoreDefinitions: ScoreDefinitionDTO[],
) {
  if (
    isNullOrUndefined(prevPlateData.layers) ||
    isNullOrUndefined(prevPlateData.ingredients) ||
    isNullOrUndefined(prevPlateData.scores)
  ) {
    return;
  }
  const prevLayer = prevPlateData.layers.find((lyr) => lyr.name === layerName);
  if (isNullOrUndefined(prevLayer)) {
    return;
  }

  const prevWell = prevPlateData.ingredients.wellList.find(
    (wl) => wl.wellNumber === wellNumber,
  );
  if (isNullOrUndefined(prevWell)) {
    return;
  }

  const prevWellLayer = prevWell.wellLayerList.find(
    (wll) => wll.layerId === prevLayer.id,
  );
  if (isNullOrUndefined(prevWellLayer)) {
    return;
  }

  const prevIngredient = prevWellLayer.wellLayerIngredientList.find(
    (wlIng) =>
      wlIng.ingredientStockId ===
      ingredientInfo.sourceIngredientRow.ingredient.ingredientStockId,
  );
  if (isNullOrUndefined(prevIngredient)) {
    return;
  }

  ingredientInfo.previousIngredientRow = {
    ingredient: prevIngredient,
    layerName: layerName,
    wellNumber: -1,
  };

  const latestInspectionId = getLatestImageBatchId(docData);
  const imageScore = getImageScores(
    prevPlateData.scores as ScoresOfWellDrops[][],
    iterativeScoregroupId,
    latestInspectionId,
    wellNumber,
  );
  if (isNullOrUndefined(imageScore)) {
    setDefaultScore(ingredientInfo, scoreDefinitions);
  } else {
    //set Default Score

    const wellDropScoreRow = scoreDefinitions.find(
      (s) => s.wellDropScoreId === imageScore.wellDropScoreId,
    );
    if (!isNullOrUndefined(wellDropScoreRow.isoValue)) {
      ingredientInfo.sourceScoreValue = wellDropScoreRow.isoValue;
    } else {
      ingredientInfo.sourceScoreValue = 0;
    }

    ingredientInfo.sourceScoreKey = getScoreISOKey(wellDropScoreRow.scoreName);
  }
}

function setDefaultScore(
  ingredientInfo: ISOIngrdientInfo,
  scoreDefinitions: ScoreDefinitionDTO[],
) {
  const defaultScore: ScoreDefinitionDTO = scoreDefinitions.find(
    (sd) => sd.scoreName === 'Clear',
  );
  if (!isNullOrUndefined(defaultScore)) {
    if (!isNullOrUndefined(defaultScore.isoValue)) {
      ingredientInfo.sourceScoreValue = defaultScore.isoValue;
    } else {
      ingredientInfo.sourceScoreValue = 0;
    }

    ingredientInfo.sourceScoreKey = getScoreISOKey(defaultScore.scoreName);
  }
}

function getScoreISOKey(scoreName: string): string {
  if (scoreName == 'Clear') {
    return 'a';
  }

  if (
    scoreName == 'Dust' ||
    scoreName == 'Granular Precipitate' ||
    scoreName == 'Phase Separation'
  ) {
    return 'c';
  }

  if (scoreName == 'Full Precipitate' || scoreName == 'Good Precipitate') {
    return 'd';
  }

  if (
    scoreName == 'Microcrystalline' ||
    scoreName == 'Needles' ||
    scoreName == '2D Crystals' ||
    scoreName == '3D Crystals'
  ) {
    return 'b';
  }

  return '';
}

function getExperimentWellList(plates: Plate[]): WellIng[] {
  let wellIngs = [];
  for (let i = 0; i < plates.length; i++) {
    if (plates[i].plateNumber === 1) {
      wellIngs = plates[i].ingredients.wellList;
    }
  }
  return wellIngs;
}

export function getLatestImageBatchId(docData: ExperimentDtoClient) {
  const latestInspectionSet: InspectionSetDTO = getLatestInspection(docData);
  let latestImageBatchId;
  const firstPlateId = docData.plateDto.find((p) => p.plateNumber === 1).plateId;
  for (let i = 0; i < latestInspectionSet.groupedInspections.length; i++) {
    if (latestInspectionSet.groupedInspections[i].plateId === firstPlateId) {
      latestImageBatchId = latestInspectionSet.groupedInspections[i].imageBatchId;
    }
  }
  return latestImageBatchId;
}

async function getExperimentScoresData(plates: Plate[]): Promise<ScoresOfWellDrops[][]> {
  let scoresData = [];
  for (let i = 0; i < plates.length; i++) {
    if (plates[i].plateNumber === 1) {
      if (plates[i].plateScores) {
        scoresData = plates[i].plateScores;
      } else {
        scoresData = await new PlateService().getPlateScoreBasedOnWellsByPlateId(
          plates[i].plateId,
        );
      }
    }
  }
  return scoresData;
}

function getImageScores(
  scoresData: ScoresOfWellDrops[][],
  iterativeScoregroupId: number,
  latestInspectionId: number,
  wellNumber: number,
) {
  const wellScores = scoresData.find(
    (score) => !isNullOrUndefined(score.find((ds) => ds.wellNumber === wellNumber)),
  );
  if (!isNullOrUndefined(wellScores)) {
    return wellScores.find(
      (dropScore) =>
        dropScore.dropNumber === 1 &&
        dropScore.wellDropScoreGroupId === iterativeScoregroupId &&
        dropScore.scoreBatchId === latestInspectionId,
    );
  }
  return null;
}

export function createIterativeScreenOptimization(
  targetExp: ExperimentDtoClient,
  sourceExp: ExperimentDtoClient,
  ingredientInfoList: ISOIngrdientInfo[],
): { expData: ExperimentDtoClient; error: boolean; message: string } {
  const customLayers: LayerDTO[] = sourceExp.layers.filter(
    (l) => l.type === LayerTypeDTO.ConditionList || l.type === LayerTypeDTO.Protein,
  );
  const screenLayers: LayerDTO[] = sourceExp.layers.filter(
    (l) => l.type === LayerTypeDTO.Screen,
  );
  targetExp.layers = [];
  for (let i = 0; i < customLayers.length; i++) {
    const lyr = customLayers[i];
    const newLayer = copyLayer(lyr, targetExp);
    targetExp.layers.push(newLayer);
  }
  for (let i = 0; i < screenLayers.length; i++) {
    const lyr = screenLayers[i];
    const newLayer = copyScreenLayerToCustomlayer(lyr, targetExp.expId);
    targetExp.layers.push(newLayer);
  }
  generate(targetExp, false);
  if (targetExp.layers.length > 0) {
    setConcentrations(ingredientInfoList, targetExp);
  }
  targetExp.layers.forEach((lyr) => (lyr.dirty = true));
  generate(targetExp, false);

  const validConcentrations = validateConcentrations(ingredientInfoList, targetExp);
  if (!validConcentrations) {
    return {
      expData: null,
      error: true,
      message: 'Could not create Optimization because of invalid concentrations.',
    };
  }

  return { expData: targetExp, error: false, message: '' };
}

function setConcentrations(
  ingredientInfoList: ISOIngrdientInfo[],
  targetExp: ExperimentDtoClient,
) {
  const targetLayers = targetExp.layers;
  for (let i = 0; i < ingredientInfoList.length; i++) {
    const ingredientInfo = ingredientInfoList[i];
    if (
      ingredientInfo.LayerType === LayerTypeDTO.ConditionList ||
      ingredientInfo.LayerType === LayerTypeDTO.Screen
    ) {
      const sourceConditionIngredientRow = ingredientInfo.sourceIngredientRow;
      const targetLayer = targetLayers.find(
        (lyr) => lyr.name === sourceConditionIngredientRow.layerName,
      );
      if (!isNullOrUndefined(targetLayer)) {
        let targetConditionIngRow;
        if (ingredientInfo.LayerType === LayerTypeDTO.ConditionList) {
          targetConditionIngRow = getConditionIngredientByStockId(
            targetLayer,
            sourceConditionIngredientRow.ingredient.ingredientStockId,
            sourceConditionIngredientRow.wellNumber,
          );
        } else {
          targetConditionIngRow = getConditionIngredientByConditionNumber(
            targetExp,
            targetLayer,
            ingredientInfo.sourceIngredientRow.wellNumber,
            sourceConditionIngredientRow.ingredient.ingredientStockId,
          );
        }
        if (isNullOrUndefined(targetConditionIngRow)) {
          continue;
        }
        targetConditionIngRow.concentration = CalculateConcentration(ingredientInfo);
        ingredientInfo.targetIngredientRow = targetConditionIngRow;
      }
    }
  }
}

function CalculateConcentration(ingredientInfo: ISOIngrdientInfo): number {
  const scoreA: string = 'a';
  const scoreC: string = 'c';
  const scoreD: string = 'd';
  const scoreValue: number = ingredientInfo.sourceScoreValue;
  const x2: number = ingredientInfo.sourceIngredientRow.ingredient.concentration;

  //When source plate is Mother plate.
  if (isNullOrUndefined(ingredientInfo.previousIngredientRow)) {
    const targetConc: number = x2 + (x2 * scoreValue) / 100;
    return targetConc;
  } else {
    const srcKey: string = ingredientInfo.sourceScoreKey;
    const prvKey: string = ingredientInfo.previousScoreKey;
    const x1: number = ingredientInfo.previousIngredientRow.ingredient.concentration;
    let conc: number = ingredientInfo.sourceIngredientRow.ingredient.concentration;

    if (prvKey == scoreA && srcKey == scoreA) {
      conc = x2 + (x2 - x1) * 1;
    } else if (prvKey == scoreA && srcKey == scoreC) {
      conc = x2 - ((x2 - x1) * 1) / 4;
    } else if (prvKey == scoreA && srcKey == scoreD) {
      conc = x2 - ((x2 - x1) * 1) / 2;
    } else if (prvKey == scoreC && srcKey == scoreA) {
      conc = x2 - ((x2 - x1) * 3) / 4;
    } else if (prvKey == scoreC && srcKey == scoreC) {
      conc = x2 + (x2 - x1) * 1;
    } else if (prvKey == scoreC && srcKey == scoreD) {
      conc = x2 + (x2 - x1) * 2;
    } else if (prvKey == scoreD && srcKey == scoreA) {
      conc = x2 - ((x2 - x1) * 1) / 2;
    } else if (prvKey == scoreD && srcKey == scoreC) {
      conc = x2 + ((x2 - x1) * 1) / 2;
    } else if (prvKey == scoreD && srcKey == scoreD) {
      conc = x2 + (x2 - x1) * 1;
    }

    return conc;
  }
}

function getConditionIngredientByStockId(
  layer: LayerDTO,
  ingStockId: number,
  wellNumber: number,
): ConditionIngredientDTO {
  if (layer.type === LayerTypeDTO.ConditionList) {
    const conditions = (layer.layerDetail as CustomLayerDTO).condition;
    return conditions
      .filter((c) => c.conditionNumber == wellNumber)
      .flatMap((c) => c.conditionIngredient)
      .find((ci) => ci.ingredientStockId === ingStockId);
  }
  return null;
}

export function copyScreenLayerToCustomlayer(
  srcLayer: LayerDTO,
  expId: number,
  copyForIso = true,
): LayerDTO {
  const screenWells = copyForIso
    ? (srcLayer.layerDetail as ScreenLayerDTO).screenLot.experiment.plateDto.find(
        (p) => p.plateNumber === 1,
      ).ingredients.wellList
    : (srcLayer.layerDetail as ScreenLayerDTO).screenLot.experiment.plateDto
        .flatMap((p) => p.ingredients)
        .flatMap((p) => p.wellList)
        .filter((wl) => wl.wellLayerList.length > 0);

  const newLayer = copyAsCustomLayer(srcLayer, expId);
  const newLayerDetail: CustomLayerDTO = new CustomLayerDTO();
  newLayerDetail.id = getNewId();
  newLayerDetail.layerId = newLayer.id;
  newLayerDetail.experimentId = expId;
  newLayerDetail.condition = [];
  newLayerDetail.wellVolume = copyForIso
    ? (srcLayer.layerDetail as ScreenLayerDTO).volume
    : srcLayer.volume;
  for (let i: number = 0; i < screenWells.length; i++) {
    const condition = new ConditionDTO();
    condition.id = getNewId();
    condition.conditionListLayerId = newLayerDetail.id;
    condition.conditionNumber = i + 1;
    condition.conditionIngredient = [];

    screenWells[i].wellLayerList.forEach((wl: WellLayerDTO) => {
      wl.wellLayerIngredientList.forEach((wli: WellLayerIngredientDTO) => {
        const conditionIng = new ConditionIngredientDTO();
        conditionIng.id = getNewId();
        conditionIng.conditionId = condition.id;
        conditionIng.concentration = wli.concentration;
        conditionIng.highPhingredientStockId = wli.highPhIngStockId;
        conditionIng.ingredientIngredientTypeId = wli.ingredientIngredientTypeId;
        conditionIng.ingredientStockId = wli.ingredientStockId;
        conditionIng.pH = wli.pH;
        condition.conditionIngredient.push(conditionIng);
      });
    });
    newLayerDetail.condition.push(condition);
  }
  newLayer.layerDetail = newLayerDetail;
  return newLayer;
}

function copyAsCustomLayer(sourceLayer: LayerDTO, expId: number): LayerDTO {
  const newLayer = new LayerDTO();
  newLayer.id = getNewId();

  newLayer.name = sourceLayer.name;
  newLayer.x = sourceLayer.x;
  newLayer.y = sourceLayer.y;
  newLayer.height = sourceLayer.height;
  newLayer.width = sourceLayer.width;

  newLayer.volume = sourceLayer.volume;
  newLayer.targetDrop = sourceLayer.targetDrop;
  newLayer.overrideLayer = sourceLayer.overrideLayer;
  newLayer.locked = sourceLayer.locked;
  newLayer.expId = expId;
  newLayer.layerState = LayerStateDTO.New;
  newLayer.volumeBasedAddition = sourceLayer.volumeBasedAddition;
  newLayer.dirty = true;
  newLayer.type = LayerTypeDTO.ConditionList;

  return newLayer;
}

function validateConcentrations(
  ingredientInfoList: ISOIngrdientInfo[],
  targetExp: ExperimentDtoClient,
): boolean {
  const messageList: string[] = [];
  const conditionListlayers = targetExp.layers.filter(
    (lyr) => lyr.type === LayerTypeDTO.ConditionList,
  );
  let errorFound: boolean = false;
  for (let i = 0; i < conditionListlayers.length; i++) {
    const layer = conditionListlayers[i];
    let errorConcentration: boolean = false;
    const targetConditions = (layer.layerDetail as CustomLayerDTO).condition;
    for (let j = 0; j < targetConditions.length; j++) {
      for (let k = 0; k < targetConditions[j].conditionIngredient.length; k++) {
        const targetIng = targetConditions[j].conditionIngredient[k];
        if (
          getIngredientTypeName(targetIng.ingredientIngredientTypeId) !== 'Precipitant'
        ) {
          continue;
        }
        const stockConcentration = getIngredientStock(
          targetIng.ingredientStockId,
        ).stockConcentration;
        if (targetIng.concentration > stockConcentration) {
          messageList.push(
            concentrationMessage(
              targetIng,
              layer.name,
              targetConditions[j].conditionNumber,
            ),
          );
          targetIng.concentration = stockConcentration;

          errorFound = true;
          errorConcentration = true;
        } else if (targetIng.concentration < 0) {
          messageList.push(
            concentrationMessage(
              targetIng,
              layer.name,
              targetConditions[j].conditionNumber,
            ),
          );
          targetIng.concentration = 0;

          errorFound = true;
          errorConcentration = true;
        }
      }
      if (!errorConcentration) {
        const wellList = targetExp.plateDto.find((p) => p.plateNumber === 1).ingredients
          .wellList;
        let wellLayerList: WellLayer[] = [];
        wellList.forEach((wl) => {
          const wellLayers = wl.wellLayerList.filter(
            (wll) => wll.layerId === layer.id && wll.waterVolume < 0,
          );
          wellLayerList = [...wellLayerList, ...wellLayers];
        });
        if (wellLayerList.length > 0) {
          for (let i = 0; i < wellLayerList.length; i++) {
            const conditionNumber = parseInt(
              wellLayerList[i].sourceWellConditionNumber.substring(
                wellLayerList[i].sourceWellConditionNumber.indexOf(' ') + 1,
              ),
            );
            const conditionRow = (layer.layerDetail as CustomLayerDTO).condition.find(
              (c) => c.conditionNumber === conditionNumber,
            );
            for (let j = 0; j < conditionRow.conditionIngredient.length; j++) {
              const conditionIng = conditionRow.conditionIngredient[j];
              if (
                getIngredientTypeName(conditionIng.ingredientIngredientTypeId) ===
                'Precipitant'
              ) {
                continue;
              }
              const targetIngInfo = ingredientInfoList.find(
                (ii) => ii.targetIngredientRow.id === conditionIng.id,
              );
              if (isNullOrUndefined(targetIngInfo)) {
                continue;
              }

              const oldConcentration: number =
                targetIngInfo.sourceIngredientRow.ingredient.concentration;
              messageList.push(
                concentrationMessage(conditionIng, layer.name, conditionNumber),
              );
              conditionIng.concentration = oldConcentration;
            }
          }
          errorFound = true;
        }
      }
    }
  }
  return !errorFound;
}

function concentrationMessage(
  conditionIngredientRow: ConditionIngredientDTO,
  layerName: string,
  conditionNumber: number,
): string {
  const ingLocation: string =
    '\nin Condition No.: ' + conditionNumber + " in Layer: '" + layerName + "'";
  return (
    'WARNING! It was not possible to reach ' +
    conditionIngredientRow.concentration +
    " concentration of ingredient '" +
    layerName +
    "' " +
    ingLocation +
    '.'
  );
}

export function hasPrecipetantIngredient(docData: ExperimentDtoClient): boolean {
  const conditionListLayers = docData.layers.filter(
    (l) => l.type === LayerTypeDTO.ConditionList,
  );
  const screenLayers = docData.layers.filter((l) => l.type === LayerTypeDTO.Screen);
  if (conditionListLayers.length > 0) {
    for (let i = 0; i < conditionListLayers.length; i++) {
      const lyr = conditionListLayers[i];
      for (let j = 0; j < (lyr.layerDetail as CustomLayerDTO).condition.length; j++) {
        const condition = (lyr.layerDetail as CustomLayerDTO).condition[j];
        for (let k = 0; k < condition.conditionIngredient.length; k++) {
          const ing = condition.conditionIngredient[k];
          if (getIngredientTypeName(ing.ingredientIngredientTypeId) === 'Precipitant') {
            return true;
          }
        }
      }
    }
    return false;
  } else if (screenLayers.length > 0) {
    for (let i = 0; i < screenLayers.length; i++) {
      const layer = screenLayers[i];
      const wellList = getExperimentWellList(docData.plateDto).filter(
        (wl) =>
          !isNullOrUndefined(wl.wellLayerList.find((wll) => wll.layerId === layer.id)),
      );

      for (let j = 0; j < wellList.length; j++) {
        const wellLayerRow = wellList[j].wellLayerList.find(
          (wll) => wll.layerId === layer.id,
        );
        for (let k = 0; k < wellLayerRow.wellLayerIngredientList.length; k++) {
          if (
            getIngredientTypeName(
              wellLayerRow.wellLayerIngredientList[k].ingredientIngredientTypeId,
            ) === 'Precipitant'
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }
  return false;
}
export function getISOConditionsList(
  docData: ExperimentDtoClient,
): OptimizationIngredientName[] {
  const ingredients: OptimizationIngredientName[] = [];
  docData.plateDto
    .find((p) => p.plateNumber === 1)
    .ingredients.wellList.forEach((well) => {
      well.wellLayerList.forEach((wll) => {
        wll.wellLayerIngredientList.forEach((wellIng) => {
          const name = getIngredientName(wellIng.ingredientIngredientTypeId);
          const stock = wellIng.ingredientStock.stockNumber;
          if (
            ingredients.findIndex((ing) => ing.name == name && ing.stockNumber == stock) <
            0
          ) {
            ingredients.push({ name: name, stockNumber: stock });
          }
        });
      });
    });
  return ingredients;
}

function getConditionIngredientByConditionNumber(
  targetExp: ExperimentDtoClient,
  layer: LayerDTO,
  wellNumber: number,
  stockId: number,
) {
  let conditionIng;
  const wellLayerList = targetExp.plateDto
    .find((p) => p.plateNumber === 1)
    .ingredients.wellList.find((w) => w.wellNumber === wellNumber).wellLayerList;
  if (wellLayerList.length > 0) {
    const conditionNumber = wellLayerList[0].sourceWellConditionNumber;
    const condition = (layer.layerDetail as CustomLayerDTO).condition.find(
      (c) => c.conditionNumber + '' == conditionNumber,
    );
    if (!isNullOrUndefined(condition)) {
      conditionIng = condition.conditionIngredient.find(
        (cIng) => cIng.ingredientStockId == stockId,
      );
    }
  }
  return conditionIng;
}
